import * as React from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  /** Allow typing custom values and pressing Enter to add them */
  allowCustom?: boolean;
  /**
   * Fetch options as the user types (debounced). When set, the dropdown shows
   * these results instead of filtering `options` locally — for vocabularies
   * that live on a remote service rather than in the bundle.
   */
  onSearch?: (query: string) => Promise<ComboboxOption[]>;
  /** Characters needed before onSearch fires. */
  minSearchLength?: number;
  /** 'sm' is the compact field for dense surfaces such as the Find Leads filter sidebar. */
  size?: 'default' | 'sm';
}

function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  emptyMessage = 'No results found.',
  className,
  disabled = false,
  allowCustom = false,
  onSearch,
  minSearchLength = 2,
  size = 'default',
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [remoteOptions, setRemoteOptions] = React.useState<ComboboxOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  // Keyboard highlight. -1 means nothing highlighted, so Enter falls through
  // to committing typed text rather than picking a suggestion the user never
  // looked at.
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const listId = React.useId();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  // Remember labels for values that came from a remote lookup, so a selected
  // chip keeps its human label once the result list has moved on.
  const labelCache = React.useRef<Map<string, string>>(new Map());
  for (const option of [...options, ...remoteOptions]) labelCache.current.set(option.value, option.label);

  const query = search.trim();

  const filtered = onSearch
    ? remoteOptions
    : options.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()));

  // A new candidate list means the old highlight points at the wrong row.
  const filteredKey = filtered.map((o) => o.value).join('\u0000');
  React.useEffect(() => {
    setActiveIndex(-1);
  }, [filteredKey, open]);

  React.useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  React.useEffect(() => {
    if (!onSearch) return;
    if (query.length < minSearchLength) {
      setRemoteOptions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await onSearch(query);
        if (!cancelled) setRemoteOptions(results);
      } catch {
        if (!cancelled) setRemoteOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, onSearch, minSearchLength]);

  // Close on click outside
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const commitPending = React.useCallback(() => {
    if (!allowCustom) return;
    const trimmed = search.trim();
    if (!trimmed) return;
    if (!value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setSearch('');
  }, [allowCustom, search, value, onChange]);

  const selectOption = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue]
    );
    // Picking a suggestion consumes the text that found it. Leaving it in the
    // input showed a stale, half-visible query next to the new chip and, on
    // blur, committed that leftover as a second custom chip.
    setSearch('');
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (filtered.length === 0) return;
      setActiveIndex((i) => (i + 1) % filtered.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filtered.length === 0) return;
      setActiveIndex((i) => (i <= 0 ? filtered.length - 1 : i - 1));
      return;
    }
    if (e.key === 'Enter') {
      // A highlighted suggestion wins; otherwise Enter commits typed text.
      if (open && activeIndex >= 0 && activeIndex < filtered.length) {
        e.preventDefault();
        selectOption(filtered[activeIndex].value);
        return;
      }
      if (allowCustom && search.trim()) {
        e.preventDefault();
        commitPending();
      }
      return;
    }
    if (e.key === 'Tab' && allowCustom && search.trim()) {
      e.preventDefault();
      commitPending();
      return;
    }
    // Backspace on empty input removes last tag
    if (e.key === 'Backspace' && search === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const removeOption = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionValue));
  };

  return (
    <div ref={containerRef} className={cn('relative', className)} onKeyDown={handleKeyDown}>
      {/* Input container styled as a single field with tags + input inline */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-1 w-full border border-control-border bg-input-bg shadow-lg transition-[outline] cursor-text mb-1.5',
          size === 'sm' ? 'min-h-9 rounded-2xl px-1.5 py-1' : 'min-h-12 rounded-3xl px-2 py-2',
          open && 'outline outline-2 outline-focus-ring outline-offset-2',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        onClick={() => {
          if (!disabled) {
            inputRef.current?.focus();
            setOpen(true);
          }
        }}
      >
        {value.map((v) => {
          const label = options.find((o) => o.value === v)?.label || labelCache.current.get(v) || v;
          return (
            <span
              key={v}
              className={cn(
                'inline-flex items-center gap-1 bg-info-surface-strong text-info-foreground-deep rounded-md shrink-0 cursor-pointer hover:bg-info-surface-hover transition-colors',
                size === 'sm' ? 'pl-2 pr-1 py-0.5 text-xs' : 'pl-2.5 pr-1.5 py-1 text-sm',
              )}
              onClick={(e) => removeOption(v, e)}
            >
              {label}
              <XMarkIcon className="h-3.5 w-3.5" />
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : ''}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          // Commit pending text on blur so the chip materialises if the user
          // tabs/clicks away without pressing Enter — a common save pitfall.
          onBlur={() => commitPending()}
          // pl-3 only when there are no chips, so the placeholder sits at ~20px
          // from the left edge to match the Select trigger's `pl-5` feel.
          // Once chips render, drop the extra padding so the caret sits flush
          // against the last chip.
          className={cn(
            'flex-1 min-w-[60px] bg-transparent outline-none placeholder:text-muted-foreground',
            size === 'sm' ? 'text-sm' : 'text-base md:text-sm',
            value.length === 0 && (size === 'sm' ? 'pl-2.5' : 'pl-3'),
          )}
        />
      </div>

      {/* Dropdown list */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-control-border bg-popover shadow-lg overflow-hidden">
          <div ref={listRef} id={listId} role="listbox" className="max-h-80 overflow-y-auto p-1.5">
            {loading && filtered.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground text-center">Searching…</p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                {onSearch && query.length < minSearchLength
                  ? `Type at least ${minSearchLength} characters`
                  : allowCustom && query
                    ? 'Press Enter to add "' + query + '"'
                    : emptyMessage}
              </p>
            ) : (
              filtered.map((option, index) => {
                const isSelected = value.includes(option.value);
                const isActive = index === activeIndex;
                return (
                  <button
                    key={option.value}
                    id={`${listId}-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={isSelected}
                    type="button"
                    className={cn(
                      'flex w-full items-center justify-between px-3 py-2 text-sm cursor-pointer select-none rounded-full',
                      'hover:bg-accent',
                      isActive && 'bg-accent',
                      isSelected && 'font-medium',
                    )}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectOption(option.value);
                    }}
                  >
                    <span>{option.label}</span>
                    {isSelected && (
                      <CheckIcon className="h-4 w-4 text-info-foreground shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { Combobox };
